import * as Context from "effect/Context";
import * as Schema from "effect/Schema";
import * as HttpApi from "effect/unstable/httpapi/HttpApi";
import * as HttpApiEndpoint from "effect/unstable/httpapi/HttpApiEndpoint";
import * as HttpApiGroup from "effect/unstable/httpapi/HttpApiGroup";
import * as HttpApiMiddleware from "effect/unstable/httpapi/HttpApiMiddleware";
import * as HttpServerRespondable from "effect/unstable/http/HttpServerRespondable";
import * as HttpServerResponse from "effect/unstable/http/HttpServerResponse";
import { EnvironmentAuthorizationError, ServerScope, ServerSessionState, ServerTokenRefreshInput, ServerTokenRefreshResult, ServerWebSocketTicketResult, } from "./authorization.js";
import { TrimmedNonEmptyString } from "./baseSchemas.js";
import { ExecutionEnvironmentDescriptor } from "./environment.js";
import { ClientOrchestrationCommand, ClientOrchestrationReadModel, DispatchResult, OrchestrationDispatchCommandError, } from "./orchestration.js";
/**
 * Every authenticated route takes a bearer server token, without
 * proof-of-possession: its threat model — binding a token against
 * interception across devices sharing one trust domain — does not apply inside
 * a loopback-bound, single-user boundary.
 */
const OptionalBearerHeaders = Schema.Struct({
    authorization: Schema.optionalKey(Schema.String),
});
export const EnvironmentRequestInvalidReason = Schema.Literals([
    "invalid_command",
    "invalid_token",
]);
export const EnvironmentAuthInvalidReason = Schema.Literals([
    "missing_credential",
    "invalid_credential",
    "platform_not_allowed",
]);
export const EnvironmentInternalErrorReason = Schema.Literals([
    "websocket_ticket_issuance_failed",
    "token_refresh_failed",
    "orchestration_snapshot_failed",
    "orchestration_dispatch_failed",
    "inference_credential_store_failed",
    "internal_error",
]);
export class EnvironmentRequestInvalidError extends Schema.TaggedError()("EnvironmentRequestInvalidError", {
    code: Schema.Literal("invalid_request"),
    reason: EnvironmentRequestInvalidReason,
    traceId: TrimmedNonEmptyString,
}, { httpApiStatus: 400 }) {
    [HttpServerRespondable.symbol]() {
        return HttpServerResponse.schemaJson(EnvironmentRequestInvalidError)(this, { status: 400 });
    }
}
export class EnvironmentAuthInvalidError extends Schema.TaggedError()("EnvironmentAuthInvalidError", {
    code: Schema.Literal("auth_invalid"),
    reason: EnvironmentAuthInvalidReason,
    traceId: TrimmedNonEmptyString,
}, { httpApiStatus: 401 }) {
    [HttpServerRespondable.symbol]() {
        return HttpServerResponse.schemaJson(EnvironmentAuthInvalidError)(this, { status: 401 });
    }
}
export class EnvironmentScopeRequiredError extends Schema.TaggedError()("EnvironmentScopeRequiredError", {
    code: Schema.Literal("insufficient_scope"),
    requiredScope: ServerScope,
    traceId: TrimmedNonEmptyString,
}, { httpApiStatus: 403 }) {
    [HttpServerRespondable.symbol]() {
        return HttpServerResponse.schemaJson(EnvironmentScopeRequiredError)(this, { status: 403 });
    }
}
export class EnvironmentInternalError extends Schema.TaggedError()("EnvironmentInternalError", {
    code: Schema.Literal("internal_error"),
    reason: EnvironmentInternalErrorReason,
    traceId: TrimmedNonEmptyString,
}, { httpApiStatus: 500 }) {
    [HttpServerRespondable.symbol]() {
        return HttpServerResponse.schemaJson(EnvironmentInternalError)(this, { status: 500 });
    }
}
export const EnvironmentHttpCommonError = Schema.Union([
    EnvironmentRequestInvalidError,
    EnvironmentAuthInvalidError,
    EnvironmentScopeRequiredError,
    EnvironmentInternalError,
]);
const EnvironmentAuthenticationErrors = [
    EnvironmentAuthInvalidError,
    EnvironmentInternalError,
];
const EnvironmentScopedOperationErrors = [
    EnvironmentScopeRequiredError,
    EnvironmentInternalError,
];
const EnvironmentTokenRefreshErrors = [
    EnvironmentRequestInvalidError,
    EnvironmentAuthInvalidError,
    EnvironmentInternalError,
];
const EnvironmentOrchestrationDispatchErrors = [
    OrchestrationDispatchCommandError,
    EnvironmentAuthorizationError,
    EnvironmentRequestInvalidError,
    EnvironmentScopeRequiredError,
    EnvironmentInternalError,
];
export class EnvironmentAuthenticatedPrincipal extends Context.Service()("@t3tools/contracts/environmentHttp/EnvironmentAuthenticatedPrincipal") {
}
export class EnvironmentAuthenticatedAuth extends HttpApiMiddleware.Service()("EnvironmentAuthenticatedAuth", {
    error: EnvironmentAuthenticationErrors,
}) {
}
export class EnvironmentMetadataHttpApi extends HttpApiGroup.make("metadata").add(HttpApiEndpoint.get("descriptor", "/.well-known/t3/environment", {
    success: ExecutionEnvironmentDescriptor,
})) {
}
export class EnvironmentAuthHttpApi extends HttpApiGroup.make("auth")
    .add(HttpApiEndpoint.get("session", "/api/auth/session", {
    headers: OptionalBearerHeaders,
    success: ServerSessionState,
    error: [EnvironmentInternalError],
}))
    .add(HttpApiEndpoint.post("webSocketTicket", "/api/auth/websocket-ticket", {
    headers: OptionalBearerHeaders,
    success: ServerWebSocketTicketResult,
    error: [EnvironmentInternalError],
}).middleware(EnvironmentAuthenticatedAuth))
    .add(HttpApiEndpoint.post("refreshToken", "/api/auth/refresh", {
    headers: OptionalBearerHeaders,
    payload: ServerTokenRefreshInput,
    success: ServerTokenRefreshResult,
    error: EnvironmentTokenRefreshErrors,
}).middleware(EnvironmentAuthenticatedAuth)) {
}
export class EnvironmentOrchestrationHttpApi extends HttpApiGroup.make("orchestration")
    .add(HttpApiEndpoint.get("snapshot", "/api/orchestration/snapshot", {
    headers: OptionalBearerHeaders,
    success: ClientOrchestrationReadModel,
    error: EnvironmentScopedOperationErrors,
}).middleware(EnvironmentAuthenticatedAuth))
    .add(HttpApiEndpoint.post("dispatch", "/api/orchestration/dispatch", {
    headers: OptionalBearerHeaders,
    payload: ClientOrchestrationCommand,
    success: DispatchResult,
    error: EnvironmentOrchestrationDispatchErrors,
}).middleware(EnvironmentAuthenticatedAuth)) {
}
/**
 * The signed-in renderer hands the server its user's `dvk_` inference
 * credential.
 *
 * The renderer is the party that can obtain one: `POST /api/me/keys` on
 * platform-api is session-cookie gated, and the server holds no session. So the
 * credential is pushed inward, which also keeps the server's token audience
 * honest — replaying an `aud=dovod-server` token
 * against platform-api would be token confusion.
 *
 * The server keeps it in memory, under the user the bearer token names, and
 * spawns that user's provider processes with it. It stays out of the server's
 * state directory: the renderer can always re-issue, and a credential on
 * disk outlives the session that authorized it.
 *
 * The payload can only carry a non-empty key, so pushing is not a way to drop
 * one. `DELETE` is: sign-out, revocation, and an account transition whose key
 * issuance failed all have to leave the server holding nothing for that user,
 * or the credential stays live and the user's next turn bills against it.
 */
export const ServerInferenceCredentialInput = Schema.Struct({
    /** Plaintext `dvk_…`. Never logged, never persisted by the server. */
    credential: TrimmedNonEmptyString,
});
export const ServerInferenceCredentialResult = Schema.Struct({
    /** Whether the server holds a credential for the caller. Carries no key material. */
    held: Schema.Boolean,
});
export class EnvironmentInferenceHttpApi extends HttpApiGroup.make("inference")
    .add(HttpApiEndpoint.post("setCredential", "/api/inference/credential", {
    headers: OptionalBearerHeaders,
    payload: ServerInferenceCredentialInput,
    success: ServerInferenceCredentialResult,
    error: EnvironmentScopedOperationErrors,
}).middleware(EnvironmentAuthenticatedAuth))
    .add(HttpApiEndpoint.delete("clearCredential", "/api/inference/credential", {
    headers: OptionalBearerHeaders,
    success: ServerInferenceCredentialResult,
    error: EnvironmentScopedOperationErrors,
}).middleware(EnvironmentAuthenticatedAuth)) {
}
export class EnvironmentHttpApi extends HttpApi.make("environment")
    .add(EnvironmentMetadataHttpApi)
    .add(EnvironmentAuthHttpApi)
    .add(EnvironmentInferenceHttpApi)
    .add(EnvironmentOrchestrationHttpApi) {
}
